/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useCallback, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { safeSetLang } from "@/helper/safeLocalStorage";
import { rememberLanguageForForum } from "@/helper/languageCookie";
import { LANGUAGES } from "@/components/common/HeaderComponents/constants";
import LanguageSwitcher from "@/components/common/HeaderComponents/LanguageSwitcher";
import { STATIC_ASSETS } from "@/constants/staticAssets";

/**
 * AL-3 / R5 (Session 447): the shell around a page that deliberately renders
 * neither the marketing navigation bar nor the site footer.
 *
 * This is AuthLayout's band, generalised rather than copied. That band was
 * built for the seven /auth pages and it had already solved the two problems
 * a standalone page runs into: a page with no route back to the site is a
 * dead end, and a visitor who lands on one in a language they cannot read has
 * no way to change it. Both answers are here, once.
 *
 * `prefix` decides the class names, so the auth pages keep the exact markup
 * and stylesheet they already had (`auth-shell`, `auth-shell-header`, …) and
 * a second caller gets its own skin without either one inheriting rules
 * written for the other.
 *
 * `footerBar` adds the thin row My PC Info needs and the auth pages do not.
 * It carries the two controls that cannot be dropped from a page that takes a
 * cookie decision in its own right - see the note on that prop.
 */
export interface StandaloneLayoutProps {
  /** Class-name prefix: `auth` yields `auth-shell`, `ds` yields `ds-shell`. */
  prefix: string;
  /** Show the language control in the band. */
  showLanguage?: boolean;
  /**
   * Render the minimal footer row.
   *
   * R5 asked for the navigation bar and the footer to be removed so the page
   * becomes standalone, and the intent is right: My PC Info is a focused
   * utility, it is reached directly from a search engine, and the site chrome
   * is not what the visitor came for.
   *
   * It cannot be taken as written on THAT page, for one specific reason. The
   * only control for changing cookie preferences lives in the site footer -
   * the button that dispatches `ds:show-cookie-preferences`, which
   * CookieConsent listens for - and My PC Info is the one page on the site
   * that asks a visitor for a cookie decision in its own right, for the map.
   * Remove the footer there and a visitor who consents to the map, and later
   * wants to withdraw that consent, has no way to do it on the page that
   * asked them. Withdrawing has to be as straightforward as giving.
   *
   * So the row carries three things and nothing else: the cookie control,
   * which is required rather than preferred; a privacy policy link, which a
   * page collecting IP and browser information should not be without; and the
   * logo above, which is the route back to the site. No navigation menu, no
   * cart, no sign-in, no footer sitemap - removed by not being here rather
   * than hidden with a stylesheet rule, which is the fragile way and the way
   * that leaves the markup for a screen reader to walk through.
   */
  footerBar?: boolean;
}

const StandaloneLayout: React.FC<StandaloneLayoutProps> = ({
  prefix,
  showLanguage = true,
  footerBar = false,
}) => {
  const { t, i18n } = useTranslation();

  /*
   * Initialised from the language the page is actually rendered in, not from
   * the stored preference. The two are not the same value and the difference
   * is visible: the stored preference is the weakest of the four signals the
   * site resolves a language from, so a page opened at /fr/... with English
   * stored renders in French while the control says English.
   */
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    const active = (i18n.language || "en").slice(0, 2).toLowerCase();
    return LANGUAGES.some((l) => l.id === active) ? active : "en";
  });

  const handleLanguage = useCallback((lang: string): void => {
    if (!lang) return;
    if (!LANGUAGES.some((l) => l.id === lang)) {
      setSelectedLanguage("en");
      safeSetLang("en");
      return;
    }

    setSelectedLanguage(lang);
    safeSetLang(lang);
    // The visitor picked it: the support forum opens in it too.
    rememberLanguageForForum(lang);

    let parts = window.location.pathname.split("/");
    if (LANGUAGES.some((l) => l.id === parts[1])) parts = parts.slice(2);
    const path = parts.filter(Boolean).join("/");

    // The query string must survive the language change. Without this,
    // changing language on the reset-password page discards the token and on
    // the OAuth callback discards the state parameter - both leave the
    // customer on a page that cannot complete what it was opened to do.
    const search = window.location.search;

    window.location.replace(
      lang === "en" ? `/${path}${search}` : `/${lang}/${path}${search}`,
    );
  }, []);

  return (
    <div className={`${prefix}-shell`}>
      <header className={`${prefix}-shell-header`}>
        {/* Session 519 review, VM-3: the band stays full width; this row holds
            its content, centred and capped like the home page's header. */}
        <div className={`${prefix}-shell-header-inner`}>
          {/* The anchor carries the accessible name and the image's alt is
              empty, so the logo is announced once rather than twice. The link
              is language-aware - a German visitor goes to the German home
              page. */}
          <Link
            to={AddLangToPath("/")}
            className={`${prefix}-shell-logo`}
            aria-label={t("accessibility.deskshare_logo")}
          >
            <img src={STATIC_ASSETS.logoSvg} alt="" width="150" />
          </Link>
          {showLanguage && (
            <LanguageSwitcher
              selectedLanguage={selectedLanguage}
              onLanguageChange={handleLanguage}
            />
          )}
        </div>
      </header>

      <div className={`${prefix}-shell-body`}>
        <Outlet />
      </div>

      {footerBar && (
        <footer className={`${prefix}-shell-footer`}>
          <button
            type="button"
            className="footer-cookie-settings-btn"
            onClick={() => {
              // The same event the site footer's control dispatches, so the
              // same CookieConsent panel opens. Reusing the class as well as
              // the event keeps the two controls looking and behaving alike.
              window.dispatchEvent(
                new CustomEvent("ds:show-cookie-preferences"),
              );
            }}
          >
            {t("footer.cookie_settings")}
          </button>
          <Link to={AddLangToPath("/privacy-policy/")}>
            {t("footer.privacy_policy")}
          </Link>
        </footer>
      )}
    </div>
  );
};

export default StandaloneLayout;
